import React, { useEffect, useMemo, useState } from 'react';
import { Sparkles, Copy, Check, Building2, BookOpen } from 'lucide-react';
import { enrichmentService, MatchedReferenceDocsDebug } from '../../../services/EnrichmentService';
import { useAuth } from '../../../contexts/AuthContext';
import { useDebugMode } from '../../../contexts/DebugModeContext';
import { JsonTreeNode } from '../../common/JsonTreeViewer';

interface EnrichmentDebugChipProps {
  /** The active order's extra_data — enrichment written by the
   *  "Ground Order Enrichment" workflow step / external sync workflows. */
  extraData?: Record<string, any> | null;
  /** The active order id (external or pk) — enables the matched
   *  reference-documents section (fetched lazily when the popover opens). */
  orderId?: string | number | null;
}

/**
 * Admin-only, debug-mode-only chip in the chat header that reveals the
 * enrichment data attached to the active order (order.extra_data): the
 * resolved alt_model, each source's namespaced slice, and provenance.
 * This is exactly the payload the chat context receives via session_order.
 */
const EnrichmentDebugChip: React.FC<EnrichmentDebugChipProps> = ({ extraData, orderId }) => {
  const { currentUser } = useAuth();
  const { debugMode } = useDebugMode();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  // Matched reference documents (the same matcher QA runs) — fetched once
  // per order when the popover first opens, so the admin sees exactly which
  // references the agent uses for this vehicle.
  const [refDocs, setRefDocs] = useState<MatchedReferenceDocsDebug | null>(null);
  const [refDocsLoading, setRefDocsLoading] = useState(false);
  const [refDocsForOrder, setRefDocsForOrder] = useState<string | null>(null);
  useEffect(() => {
    const key = orderId != null ? String(orderId) : null;
    if (!open || !key || refDocsForOrder === key || refDocsLoading) return;
    setRefDocsLoading(true);
    enrichmentService.getMatchedReferenceDocs(key)
      .then((resp) => { setRefDocs(resp.data); setRefDocsForOrder(key); })
      .catch(() => { setRefDocs(null); setRefDocsForOrder(key); })
      .finally(() => setRefDocsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orderId]);

  const data = extraData && typeof extraData === 'object' ? extraData : null;
  const sources: Record<string, string> = (data?._sources && typeof data._sources === 'object')
    ? data._sources
    : {};
  const dataKeys = useMemo(
    () => Object.keys(data || {}).filter(k => !k.startsWith('_')),
    [data],
  );

  const isAdmin = currentUser?.role === 'admin';
  if (!isAdmin || !debugMode) return null;

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data ?? {}, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable — ignore */ }
  };

  return (
    <div className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        title="Debug — enrichment data on this order (order.extra_data), as the chat context sees it"
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-colors cursor-pointer ${
          open
            ? 'bg-teal-600 border-teal-600 text-white'
            : dataKeys.length > 0
              ? 'bg-teal-50 border-teal-200 text-teal-700 hover:bg-teal-100'
              : 'bg-[#f6f4f0] border-[#efebe2] text-[#999999] hover:bg-[#efebe2] hover:text-[#201e1a]'
        }`}
      >
        <Sparkles className="w-3.5 h-3.5" />
        <span className="text-xs font-medium">Enrichment</span>
        <span className={`rounded-full px-1.5 leading-4 text-[10px] font-bold ${
          open ? 'bg-white/25 text-white'
            : dataKeys.length > 0 ? 'bg-teal-600 text-white' : 'bg-[#dddcd4] text-white'
        }`}>
          {dataKeys.length}
        </span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 z-50 w-[26rem] max-w-[90vw] bg-white rounded-2xl shadow-xl border border-[#efebe2] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#efebe2]">
              <div className="flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-teal-600" />
                <span className="text-xs font-semibold text-[#201e1a]">Order enrichment (extra_data)</span>
              </div>
              {data && (
                <button
                  onClick={copyJson}
                  title="Copy JSON"
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
                >
                  {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              )}
            </div>

            <div className="max-h-[60vh] overflow-auto">
                {/* Matched reference documents — what the QA matcher selects
                    for this order's vehicle (chunks=ready means excerpts can
                    ground answers/searches). */}
                {(refDocsLoading || refDocs) && (
                  <div className="px-4 py-2.5 border-b border-[#efebe2] bg-[#fafaf7]">
                    <div className="flex items-center gap-1.5 mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                      <BookOpen className="w-3 h-3" />
                      Reference documents
                    </div>
                    {refDocsLoading ? (
                      <p className="text-[11px] text-gray-400">Matching documents…</p>
                    ) : !refDocs?.enabled ? (
                      <p className="text-[11px] text-amber-600">reference_docs_search flag is OFF (global)</p>
                    ) : (refDocs?.matched?.length ?? 0) === 0 ? (
                      <p className="text-[11px] text-gray-500">
                        No document matched — makes={JSON.stringify(refDocs?.vehicle_filter?.makes ?? [])},
                        {' '}models={JSON.stringify(refDocs?.vehicle_filter?.model_terms ?? [])}
                      </p>
                    ) : (
                      <>
                        <div className="space-y-1">
                          {refDocs!.matched.map((d) => (
                            <div key={d.source_id} className="flex items-center gap-2 text-[11px]">
                              <span className="font-medium text-[#201e1a] truncate">{d.title}</span>
                              <span className="px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-[10px] capitalize whitespace-nowrap">
                                {String(d.doc_type || 'other').replace(/_/g, ' ')}
                              </span>
                              {d.page_count ? (
                                <span className="text-gray-400 whitespace-nowrap">{d.page_count}p</span>
                              ) : null}
                              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${
                                d.chunks_ready
                                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                  : 'bg-amber-50 text-amber-700 border border-amber-200'
                              }`}>
                                {d.chunks_ready ? 'chunks ready' : 'chunks missing'}
                              </span>
                            </div>
                          ))}
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1.5 font-mono truncate"
                           title={`models=${JSON.stringify(refDocs?.vehicle_filter?.model_terms ?? [])}`}>
                          makes={JSON.stringify(refDocs?.vehicle_filter?.makes ?? [])} · models={JSON.stringify(refDocs?.vehicle_filter?.model_terms ?? [])}
                        </p>
                      </>
                    )}
                  </div>
                )}


            {!data || dataKeys.length === 0 ? (
              <div className="px-4 py-6 text-center">
                <p className="text-xs text-gray-500">
                  No enrichment data on this order yet.
                </p>
                <p className="text-[11px] text-gray-400 mt-1">
                  It is written by the "Ground Order Enrichment" workflow step when a source
                  matches this order's vehicle.
                </p>
              </div>
            ) : (
              <>
                {/* Provenance — which source@version produced each namespace */}
                {Object.keys(sources).length > 0 && (
                  <div className="px-4 py-2.5 border-b border-[#efebe2] bg-[#fafaf7]">
                    <div className="flex items-center gap-1.5 mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                      <Building2 className="w-3 h-3" />
                      Sources
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(sources).map(([ns, label]) => (
                        <span key={ns} className="px-2 py-0.5 rounded-full text-[11px] bg-teal-50 text-teal-700 border border-teal-200">
                          {String(label)} → <span className="font-mono">{ns}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* The payload itself, exactly as chat context receives it */}
                <div className="px-3 py-2 text-xs">
                  {Object.entries(data).map(([k, v]) => (
                    <JsonTreeNode key={k} nodeKey={k} value={v} depth={0} defaultExpanded={k === 'alt_model'} />
                  ))}
                </div>
              </>
            )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EnrichmentDebugChip;
